import React, { useCallback, useImperativeHandle, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { makeStyles } from '@material-ui/core/styles'
import RoundedModal from '../../../../molecules/RoundedModal'
import SydButton from '../../../../commonDesign/Button'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydSelect from '../../../../commonDesign/SydSelect'
import Loading from '../../../../molecules/Loading'
import { useGetDagStatus } from '../../../../../api/dags'
import { buildOrderedTree } from './util/buildOrderedTree'

const useStyles = makeStyles({
  warning: {
    backgroundColor: '#FFF3E0',
    border: '1px solid #FFB74D',
    borderRadius: 6,
    padding: '12px 16px',
    fontSize: 13,
    color: '#E65100',
    marginBottom: 16
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#616161',
    marginBottom: 4
  },
  fieldGroup: {
    marginBottom: 16
  }
})

function findRootStepId (steps) {
  const root = steps?.find(s => s.parentStepId == null)
  return root?.dagStepId ?? 'start'
}

const RunDagDialog = React.forwardRef(function RunDagDialog ({ onConfirm }, ref) {
  const classes = useStyles()
  const [dialogState, setDialogState] = useState({ open: false, processing: false, dag: null })
  const [selectedStepId, setSelectedStepId] = useState(null)

  const dagId = dialogState.dag?.dagId
  const { data: dagStatus, isLoading: isCheckingStatus } = useGetDagStatus(dagId, {
    enabled: dialogState.open && !!dagId
  })

  const isRunning = dagStatus?.status === 'running'

  const stepOptions = useMemo(() => {
    const steps = dialogState.dag?.steps
    if (!steps?.length) return []
    return buildOrderedTree(steps).map(({ step, outline }) => ({
      value: step.dagStepId,
      label: `${outline}. ${step.name} (${step.dagStepId})`
    }))
  }, [dialogState.dag])

  const close = useCallback(() => {
    setDialogState({ open: false, processing: false, dag: null })
    setSelectedStepId(null)
  }, [])

  const handleConfirm = useCallback(async () => {
    try {
      setDialogState((prev) => ({ ...prev, processing: true }))
      await onConfirm(dialogState.dag, selectedStepId)
    } finally {
      close()
    }
  }, [dialogState.dag, selectedStepId, onConfirm, close])

  useImperativeHandle(ref, () => ({
    open: (dag) => {
      const rootId = findRootStepId(dag?.steps)
      setSelectedStepId(rootId)
      setDialogState({ open: true, processing: false, dag })
    }
  }), [])

  return (
    <RoundedModal
      title='Run Workflow'
      open={dialogState.open}
      onClose={close}
      size='medium'
      position='top'
      disablePortal={false}
    >
      <div>
        {dialogState.open ? (
          <>
            {isCheckingStatus ? (
              <Loading />
            ) : isRunning ? (
              <>
                <div className={classes.warning}>
                  This workflow is currently running
                  {dagStatus.when
                    ? ` (started ${dayjs(dagStatus.when).format('MMM D, YYYY h:mm A')})`
                    : ''}.
                  Please wait for it to finish before starting a new run.
                </div>
                <SydModalActions>
                  <SydButton variant='ghost' size='md' onClick={close}>
                    Close
                  </SydButton>
                </SydModalActions>
              </>
            ) : (
              <>
                <p style={{ margin: '0 0 16px' }}>
                  Run workflow <strong>{dialogState.dag?.name}</strong>?
                </p>
                <div className={classes.fieldGroup}>
                  <div className={classes.fieldLabel}>Starting step</div>
                  <SydSelect
                    size='sm'
                    value={selectedStepId ?? ''}
                    options={stepOptions}
                    onChange={(e) => setSelectedStepId(e.target.value)}
                  />
                </div>
                <SydModalActions>
                  <SydButton disabled={dialogState.processing} variant='ghost' size='md' onClick={close}>
                    Cancel
                  </SydButton>
                  <SydButton
                    processing={dialogState.processing}
                    variant='primary'
                    icon='play_arrow'
                    size='md'
                    onClick={handleConfirm}
                  >
                    Run
                  </SydButton>
                </SydModalActions>
              </>
            )}
          </>
        ) : null}
      </div>
    </RoundedModal>
  )
})

RunDagDialog.propTypes = {
  onConfirm: PropTypes.func.isRequired
}

export default RunDagDialog
